import { useChatStore } from '../../store/chat';
import { formatPrice } from '../../lib/format';
import type { OfertaAlt, PlanoAlt } from '../../types/agent';

/**
 * Faixa de alternativa SUBLINGUAL anexada SOB o CotacaoCard (evento `oferta_alt`).
 * A cápsula continua sendo o orçamento PRINCIPAL; esta é uma oferta anexa opcional.
 * O preço/planos vêm do MOTOR REAL (forma="sublingual") — não de escada hardcoded.
 * Tocar num plano re-cota na versão sublingual (a Mina segue o fluxo normal).
 */
export function SublingualOffer({ oferta }: { oferta: OfertaAlt }) {
  if (!oferta?.planos?.length) return null;
  // destaque no plano de 90 dias (o "mais escolhido"), como no CotacaoCard
  const melhor = oferta.planos.find((p) => p.dias === 90) ?? oferta.planos.at(-1)!;

  function escolher(p: PlanoAlt) {
    const qtd = p.n_unidades ? ` (${p.n_unidades} comprimidos)` : '';
    useChatStore
      .getState()
      .sendMessage(
        `Quero a versão sublingual — plano de ${p.dias} dias${qtd} por ${formatPrice(p.preco)}. ✅`
      );
  }

  return (
    <div
      data-testid="sublingual-offer"
      className="mt-1.5 -mx-0.5 rounded-[10px] overflow-hidden border border-[#E6E9EC] bg-[#FBFAF7]"
    >
      {/* cabeçalho da alternativa */}
      <div className="flex items-start gap-2 px-3 pt-2.5 pb-1.5">
        <span className="text-[18px] leading-none mt-0.5" aria-hidden>
          💊
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-[#111B21]">{oferta.titulo}</div>
          <div className="text-[11.5px] text-[#667781] leading-snug">{oferta.subtitulo}</div>
        </div>
      </div>

      {/* planos sublinguais tocáveis */}
      <div className="border-t border-[#EFEAE0]">
        {oferta.planos.map((p) => {
          const destaque = p.dias === melhor.dias;
          return (
            <button
              key={p.dias}
              type="button"
              onClick={() => escolher(p)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left active:bg-[#F2ECDD] border-t border-[#EFEAE0] first:border-t-0"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#111B21]">
                  {destaque && (
                    <span className="font-semibold text-[#8A6D1F]">Mais escolhido · </span>
                  )}
                  {p.dias} dias
                  {p.n_unidades ? (
                    <span className="text-[#667781]"> · {p.n_unidades} comp.</span>
                  ) : null}
                </div>
                {p.delta_pct != null && p.delta_pct > 0 && (
                  <div className="text-[11px] text-[#8696A0]">+{p.delta_pct}% vs cápsula</div>
                )}
              </div>
              <div className="text-[14px] font-semibold text-[#075E54] tabular-nums">
                {formatPrice(p.preco)}
              </div>
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#8696A0]" aria-hidden fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>
          );
        })}
      </div>

      <div className="px-3 py-1.5 bg-[#FFF7E6] text-[11px] text-[#8A6D1F] border-t border-[#EFEAE0]">
        A versão em cápsula continua sendo o seu orçamento principal — a sublingual é opcional.
      </div>
    </div>
  );
}
